import { Table } from '@/components/commons';
import { ENDPOINT } from '@/services/endpoint';
import { ScreenMediaService } from '@/services/screenMedia';
import {
  IRotationPreviewRow,
  ScreenMediaPlacement,
} from '@/types/response/screenMedia';
import { Alert, DatePicker, InputNumber, TableColumnsType, Tag } from 'antd';
import locale from 'antd/es/date-picker/locale/ja_JP';
import moment, { Moment } from 'moment';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';

interface IProps {
  placement: ScreenMediaPlacement;
}

/**
 * どの日にどのメディアが出るかの一覧。
 *
 * 表示は「暦日を3で割った区間」で決まるため、登録した素材がいつ出るのかを
 * 頭の中で計算するのは現実的ではない。入稿後にここで確認できるようにしておく。
 */
export const RotationPreview: React.FC<IProps> = ({ placement }) => {
  const [from, setFrom] = useState<Moment>(moment());
  const [days, setDays] = useState<number>(30);

  const fromISO = from.format('YYYY-MM-DD');

  const { data, isFetching } = useQuery(
    [ENDPOINT.SCREEN_MEDIA.ROTATION_PREVIEW, placement, fromISO, days],
    () =>
      ScreenMediaService.rotationPreview({ placement, from: fromISO, days }),
  );

  const rows = useMemo(() => data?.data ?? [], [data]);

  // 同じメディアが続く区間の先頭だけ日付を強調する
  const changeDates = useMemo(() => {
    const dates = new Set<string>();
    let previous: number | null | undefined;

    for (const row of rows) {
      if (row.screenMediaId !== previous) dates.add(row.date);
      previous = row.screenMediaId;
    }

    return dates;
  }, [rows]);

  const columns: TableColumnsType<IRotationPreviewRow> = [
    {
      title: '日付',
      dataIndex: 'date',
      width: 160,
      render: (value: string) => (
        <span className={changeDates.has(value) ? 'font-semibold' : ''}>
          {moment(value).format('YYYY/MM/DD (ddd)')}
          {changeDates.has(value) && (
            <Tag color='blue' className='ml-2'>
              切替
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: '表示されるメディア',
      dataIndex: 'title',
      render: (value: string | null) =>
        value ?? <span className='text-gray-400'>表示なし</span>,
    },
    {
      title: '種別',
      dataIndex: 'mediaType',
      width: 100,
      align: 'center',
      render: (value: 'image' | 'video' | null) =>
        value === 'video' ? '動画' : value === 'image' ? '静止画' : '―',
    },
  ];

  const hasEmptyDay = rows.some((row) => row.screenMediaId === null);

  return (
    <div>
      <div className='flex items-center gap-3 mb-4'>
        <span className='text-sm'>起点</span>
        <DatePicker
          locale={locale}
          value={from}
          allowClear={false}
          onChange={(value) => value && setFrom(value)}
        />
        <span className='text-sm'>表示日数</span>
        <InputNumber
          min={1}
          max={180}
          value={days}
          onChange={(value) => setDays(value ?? 30)}
          className='w-[90px]'
        />
      </div>

      {hasEmptyDay && (
        <Alert
          type='info'
          showIcon
          className='mb-4'
          message='メディアが表示されない日があります'
          description='その日はアプリ側では従来どおりアニメーションのみが表示されます（エラーにはなりません）。'
        />
      )}

      <Table
        columns={columns}
        dataSource={rows}
        rowKey='date'
        loading={isFetching}
        pagination={false}
      />
    </div>
  );
};
